/**
 * Device Utilities for Signature Feature
 * Provides functions to capture device information and location data
 * for enhanced security and analytics in signature workflows
 */

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenSize: string;
  timezone: string;
  cookieEnabled: boolean;
  onLine: boolean;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

/**
 * Get comprehensive device information for signature security
 */
export const getDeviceInfo = (): DeviceInfo => {
  const deviceInfo: DeviceInfo = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenSize: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    connection: (navigator as any).connection ? {
      effectiveType: (navigator as any).connection.effectiveType,
      downlink: (navigator as any).connection.downlink,
      rtt: (navigator as any).connection.rtt
    } : undefined
  };

  return deviceInfo;
};

/**
 * Get device fingerprint for enhanced security
 */
export const getDeviceFingerprint = async (): Promise<string> => {
  const deviceInfo = getDeviceInfo();
  
  // Create a hash of device characteristics
  const fingerprint = [
    deviceInfo.userAgent,
    deviceInfo.platform,
    deviceInfo.screenSize,
    deviceInfo.timezone,
    deviceInfo.language,
    deviceInfo.hardwareConcurrency,
    deviceInfo.deviceMemory
  ].join('|');

  // Simple hash function (in production, use a proper hashing library)
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
};

/**
 * Get location data if available and permitted
 */
export const getLocationData = async (): Promise<LocationData | null> => {
  try {
    if (!('geolocation' in navigator)) {
      console.warn('Geolocation not available');
      return null;
    }

    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Geolocation timeout'));
      }, 10000); // 10 second timeout

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: position.timestamp
          });
        },
        (error) => {
          clearTimeout(timeoutId);
          console.warn('Geolocation error:', error);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  } catch (error) {
    console.warn('Geolocation error:', error);
    return null;
  }
};

/**
 * Check if device supports touch input
 */
export const isTouchDevice = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Get optimal signature canvas size based on device
 */
export const getSignatureCanvasSize = (): { width: number; height: number } => {
  const isTouch = isTouchDevice();
  const screenWidth = window.innerWidth;
  
  if (isTouch) {
    // Mobile devices - use larger canvas for better touch input
    return {
      width: Math.min(screenWidth - 40, 400),
      height: 200
    };
  } else {
    // Desktop devices
    return {
      width: 400,
      height: 150
    };
  }
};

/**
 * Validate device capabilities for signature capture
 */
export const validateDeviceCapabilities = (): {
  canCaptureSignature: boolean;
  warnings: string[];
} => {
  const warnings: string[] = [];
  let canCaptureSignature = true;

  // Check for basic canvas support
  if (!document.createElement('canvas').getContext) {
    canCaptureSignature = false;
    warnings.push('Canvas not supported');
  }

  // Check for touch support on mobile
  if (isTouchDevice() && !('ontouchstart' in window)) {
    warnings.push('Touch input may not work properly');
  }

  // Check for storage support
  if (!window.localStorage) {
    warnings.push('Local storage not available');
  }

  // Check for network connectivity
  if (!navigator.onLine) {
    warnings.push('No internet connection - signatures will be cached');
  }

  return { canCaptureSignature, warnings };
};

/**
 * Get network information for analytics
 */
export const getNetworkInfo = (): {
  type: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
} => {
  const connection = (navigator as any).connection;
  
  if (connection) {
    return {
      type: connection.effectiveType || 'unknown',
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt
    };
  }

  return { type: 'unknown' };
};

/**
 * Capture comprehensive device context for signature
 */
export const captureDeviceContext = async (): Promise<{
  deviceInfo: DeviceInfo;
  locationData: LocationData | null;
  fingerprint: string;
  networkInfo: any;
  capabilities: any;
}> => {
  const [locationData, fingerprint] = await Promise.all([
    getLocationData(),
    getDeviceFingerprint()
  ]);

  return {
    deviceInfo: getDeviceInfo(),
    locationData,
    fingerprint,
    networkInfo: getNetworkInfo(),
    capabilities: validateDeviceCapabilities()
  };
};

